import { useThemeStore } from 'app/hooks'
import React, { useState, createContext, ReactNode, useEffect } from 'react'
import { ThemeProvider as StyledThemeProvider } from 'styled-components'
import { theme as defaultTheme } from './theme'
import useBrandTheme from 'app/hooks/useBrandTheme'

export const lightColor = {
	bg: '#f9f9f9',
	plus: '#f05551',
	minus: '#007eda',
	text: '#000',
	subText: '#6d6d6d',
	nameBg: '#f0f0f0',
	stroke: '#e9e9e9',
	hover: '#f0f0f0',
	disabled: '#acacac',
	active: '#1151ff',
}

export const darkColor = {
	bg: '#242424',
	hover: '#333333',
	plus: '#ff5555',
	minus: '#8dc8ff',
	text: '#ffffff',
	subText: '#d3d3d3',
	stroke: '#333333',
	nameBg: '#333333',
	disabled: '#acacac',
	active: '#1151ff',
}

type ThemeContextType = {
	isDarkMode: boolean
	isMobile: boolean | null
	toggleMobile: (set?: any) => void
	toggleTheme: (set?: any) => void
	changeResize: (set?: any) => void
	canvasColor: {
		bg: string
		plus: string
		minus: string
		text: string
		subText: string
		stroke: string
		hover: string
		disabled: string
		active: string
		nameBg: string
	}
	theme: {
		COLORS: {
			textw: string
			stroke: string
			Secondary: string
			Tertiary: string
			onSecondary: string
			onTertiary: string
			Prinary: string
			complementary: string
			point: string
			fail: string
			raise: string
			fail_sub: string
			raise_sub: string
			background: string
			gray: string
			grey: string
			bgColor: string
			descBox: string
			drawerBgColor: string
			opacityBgColor: string
			textsilver: string
			textGrey: string
			textGray: string
			borderColor: string
			descBorder: string
			header: string
			subHeader: string
			shadow: string
			cardOpacity: string
			mdrawerBgColor: string
			tradeBG: string
			mitBG: string
			// 동적 브랜드 색상
			grad1: string
			grad2: string
			gradtext: string
			primary: string
			BTN_HOVER: string
			white: string
		}
	}
}
type ThemeProviderProps = {
	children: ReactNode
}
export const ThemeContext = createContext<ThemeContextType>(
	{} as ThemeContextType
)
const ThemeProvider = ({ children }: ThemeProviderProps) => {

	const [canvasColor, setCanvasColor] = useState<any>(lightColor)

	const { data: themeStore, mutate } = useThemeStore()
	
	// useSWR을 사용한 브랜드 테마 로딩
	const { brandTheme, isLoading, isError, refreshTheme } = useBrandTheme();
	
	// 동적 테마 로딩 상태
	const [activeTheme, setActiveTheme] = useState(defaultTheme);
	
	// window.BRAND_THEME을 직접 확인하는 로직 추가
	const [windowTheme, setWindowTheme] = useState(null);
	
	useEffect(() => {
		const checkWindowTheme = () => {
			const theme = (window as any).BRAND_THEME;
			if (theme && theme !== windowTheme) {
				console.log('🎨 window.BRAND_THEME 감지:', theme.name);
				setWindowTheme(theme);
			}
		};
		
		// 초기 체크
		checkWindowTheme();
		
		// 주기적 체크 (100ms마다)
		const interval = setInterval(checkWindowTheme, 100);
		
		return () => clearInterval(interval);
	}, [windowTheme]);
	
	// 브랜드 테마 적용 (useSWR과 window.BRAND_THEME 모두 체크)
	useEffect(() => {
		const applyTheme = (themeData: any, source: string) => {
			console.log(`🎨 ${source} 브랜드 테마 적용 중:`, themeData.name);
			
			if (themeData.theme && themeData.theme.light.COLORS && themeData.theme.dark.COLORS) {
				// 브랜드 스타일을 COLORS에 병합
				const brandStyles = themeData.styles || {};
				console.log('🎨 브랜드 스타일 병합:', brandStyles);
				
				const newTheme = {
					light: {
						COLORS: {
							...themeData.theme.light.COLORS,
							// 동적 그라디언트 색상 추가
							grad1: brandStyles.grad1 || '#DD48F9',
							grad2: brandStyles.grad2 || '#B91DDF', 
							gradtext: brandStyles.gradtext || '#ffffff',
							primary: brandStyles.primary || themeData.theme.light.COLORS.point,
							BTN_HOVER: brandStyles.BTN_HOVER || themeData.theme.light.COLORS.point,
							white: '#ffffff'
						}
					},
					dark: {
						COLORS: {
							...themeData.theme.dark.COLORS,
							// 동적 그라디언트 색상 추가  
							grad1: brandStyles.grad1 || '#DD48F9',
							grad2: brandStyles.grad2 || '#B91DDF',
							gradtext: brandStyles.gradtext || '#ffffff',
							primary: brandStyles.primary || themeData.theme.dark.COLORS.point,
							BTN_HOVER: brandStyles.BTN_HOVER || themeData.theme.dark.COLORS.point,
							white: '#ffffff'
						}
					}
				};
				setActiveTheme(newTheme);
				console.log(`✅ ${source} 테마 적용 완료:`, {
					lightPoint: themeData.theme.light.COLORS.point,
					darkPoint: themeData.theme.dark.COLORS.point,
					grad1: brandStyles.grad1,
					grad2: brandStyles.grad2,
				});
				return true;
			}
			return false;
		};

		// window.BRAND_THEME 우선 체크
		if (windowTheme) {
			if (applyTheme(windowTheme, 'window.BRAND_THEME')) return;
		}
		
		// useSWR 테마 체크
		if (brandTheme) {
			if (applyTheme(brandTheme, 'useSWR')) return;
		}
		
		// 에러 또는 로딩 상태 처리
		if (isError) {
			console.log('❌ 브랜드 테마 로딩 에러, 기본 테마 사용');
		} else if (!isLoading && !brandTheme && !windowTheme) {
			console.log('📁 테마 파일이 없음, 기본 테마 사용');
		}
	}, [brandTheme, windowTheme, isError, isLoading]);
	// 초기 테마를 기본 테마로 설정 (null 대신)
	const [themeCheck, setThemeCheck] = useState<any>(
		themeStore?.darkMode ? defaultTheme.light : defaultTheme.dark
	)
	
	// 동적 테마가 로딩되면 업데이트
	useEffect(() => {
		if (!isLoading) { // 로딩이 완료되면 (성공/실패 상관없이)
			const newTheme = themeStore?.darkMode ? activeTheme.light : activeTheme.dark;
			console.log('🎨 ThemeProvider 동적 테마 적용:', {
				darkMode: themeStore?.darkMode,
				pointColor: newTheme?.COLORS?.point,
				isRuntimeTheme: true,
				brandLoaded: !!brandTheme
			});
			setThemeCheck(newTheme);
		}
	}, [activeTheme, themeStore?.darkMode, isLoading, brandTheme]);

	//파트너 페이지는 항상 데스크톱 레이아웃 사용
	const ismts = false

	const [isMobile, setIsMobile] = useState<any>(false)

	const toggleMobile = set => {
		// 파트너 페이지: 항상 데스크톱 모드 유지
	}

	const toggleTheme = set => {
		const change = set !== undefined ? set : !themeStore?.darkMode

		// View Transition API 지원 확인
		if ('startViewTransition' in document) {
			// @ts-ignore - View Transition API는 실험적 기능
			document.startViewTransition(() => {
				mutate({ darkMode: change })
			})
		} else {
			// View Transition 미지원 시 오버레이 페이드 효과
			const overlay = document.createElement('div')
			overlay.style.cssText = `
				position: fixed;
				top: 0;
				left: 0;
				width: 100%;
				height: 100%;
				background: ${themeStore?.darkMode ? '#f9f9f9' : '#242424'};
				opacity: 0;
				transition: opacity 0.15s ease-in-out;
				z-index: 999999;
				pointer-events: none;
			`
			document.body.appendChild(overlay)

			// 페이드 인
			requestAnimationFrame(() => {
				overlay.style.opacity = '0.3'
			})

			// 테마 변경
			setTimeout(() => {
				mutate({ darkMode: change })

				// 페이드 아웃
				setTimeout(() => {
					overlay.style.opacity = '0'
					setTimeout(() => {
						document.body.removeChild(overlay)
					}, 150)
				}, 50)
			}, 150)
		}
	}

	const changeResize = set => {
		mutate({ isResize: set })
	}

	useEffect(() => {
		if(themeStore?.darkMode) {
			setCanvasColor(darkColor)
		} else {
			setCanvasColor(lightColor)
		}
		
		// 동적으로 로드된 테마의 CSS 변수 업데이트
		if (!isLoading && activeTheme) {
			const root = document.documentElement;
			const currentColors = themeStore?.darkMode ? activeTheme.light.COLORS : activeTheme.dark.COLORS;
			
			if (currentColors && currentColors.point) {
				root.style.setProperty('--brand-point-color', currentColors.point);
				console.log('🎨 CSS 변수 업데이트:', currentColors.point);
			}
		}
	}, [themeStore?.darkMode, activeTheme, isLoading])

	// 테마 파일 로딩 중에만 로딩 표시 (themeCheck는 항상 존재)
	if (isLoading) {
		return (
			<div style={{ 
				display: 'flex', 
				justifyContent: 'center', 
				alignItems: 'center', 
				height: '100vh',
				fontFamily: 'Noto Sans KR, sans-serif'
			}}>
				브랜드 테마 로딩 중...
			</div>
		);
	}

	return (
		<ThemeContext.Provider
			value={{
				isDarkMode: !themeStore?.darkMode,
				isMobile,
				toggleMobile,
				toggleTheme,
				theme: themeCheck,
				canvasColor,
				changeResize,
			}}>
			<StyledThemeProvider theme={themeCheck}>
				{children}
			</StyledThemeProvider>
		</ThemeContext.Provider>
	)
}

export default ThemeProvider
